import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { SEOProps } from '../components/SEO';

// Routes that should be noindex
const NOINDEX_ROUTES = [
    '/search',
    '/login',
    '/register',
    '/forgot-password',
    '/reset-password',
    '/verify-email',
    '/jobs/create',
    '/jobs/edit',
    '/jobs/my',
    '/my-quotes',
    '/messages',
    '/notifications',
    '/profile',
    '/reviews/create',
    '/provider',
    '/admin',
    '/design-system',
    '/settings',
];

// Check if route should be noindex
const shouldNoindex = (pathname: string): boolean => {
    return NOINDEX_ROUTES.some(route =>
        pathname === route || pathname.startsWith(`${route}/`)
    );
};

export interface UseSEOOptions {
    title?: string;
    description?: string;
    noindex?: boolean;
    nofollow?: boolean;
    ogType?: 'website' | 'article' | 'profile';
    ogImage?: string;
    jsonLd?: object;
}

export const useSEO = (options: UseSEOOptions = {}): SEOProps => {
    const location = useLocation();
    const { t } = useTranslation();

    return useMemo(() => {
        const pathname = location.pathname;

        // Auto-detect noindex based on route
        const autoNoindex = shouldNoindex(pathname);

        // Build canonical URL (without query params for most pages)
        const canonical = pathname;

        return {
            title: options.title,
            description: options.description || t('meta.description'),
            canonical,
            noindex: options.noindex ?? autoNoindex,
            nofollow: options.nofollow ?? false,
            ogType: options.ogType || 'website',
            ogImage: options.ogImage,
            jsonLd: options.jsonLd,
        };
    }, [location.pathname, options, t]);
};

// Pre-defined SEO configs for common pages
// NOTE: These produce static objects. For i18n, pages should use
// t('seo.xxx') directly when calling <SEO /> instead of these configs.
export const seoConfigs = {
    home: (): UseSEOOptions => ({
        title: undefined, // Page-level <SEO /> handles this with t()
        description: 'Bricolat - Trouvez des artisans de confiance au Maroc. Plombiers, électriciens, peintres et plus encore.',
    }),

    search: (): UseSEOOptions => ({
        title: 'Recherche',
        noindex: true, // Always noindex search
        nofollow: false,
    }),

    howItWorks: (): UseSEOOptions => ({
        title: 'Comment ça marche',
        description: 'Découvrez comment trouver un artisan de confiance sur Bricolat en 3 étapes simples.',
    }),

    security: (): UseSEOOptions => ({
        title: 'Sécurité & Confiance',
        description: 'Découvrez nos mesures de sécurité : profils vérifiés, avis clients authentiques et prestataires certifiés.',
    }),

    contact: (): UseSEOOptions => ({
        title: 'Contactez-nous',
        description: 'Besoin d\'aide? Contactez l\'équipe Bricolat par email, téléphone ou formulaire.',
    }),

    terms: (): UseSEOOptions => ({
        title: 'Conditions d\'utilisation',
        description: 'Consultez les conditions générales d\'utilisation de Bricolat.',
    }),

    privacy: (): UseSEOOptions => ({
        title: 'Politique de confidentialité',
        description: 'Découvrez comment Bricolat protège vos données personnelles.',
    }),

    providerProfile: (provider: { name: string; category?: string; city?: string; rating?: number; reviewCount?: number }): UseSEOOptions => ({
        title: provider.category && provider.city
            ? `${provider.name} — ${provider.category} à ${provider.city}`
            : provider.name,
        description: `${provider.name}${provider.category ? `, ${provider.category}` : ''}${provider.city ? ` à ${provider.city}` : ''}. ${provider.rating ? `Note: ${provider.rating}/5 (${provider.reviewCount} avis).` : ''} Demandez un devis gratuit sur Bricolat.`,
        ogType: 'profile',
    }),

    notFound: (): UseSEOOptions => ({
        title: 'Page non trouvée',
        noindex: true,
        nofollow: true,
    }),
};

export default useSEO;
